import React, { useState } from 'react';
import { X, Brain, Target, Clock, BarChart3, Loader, CheckCircle, AlertTriangle } from 'lucide-react';
import PromptDebugger from '../Debug/PromptDebugger';
import { Goal } from '../../types';
import { apiService } from '../../services/api';
import ContextQuestionModal from './ContextQuestionModal';
import { apiConfig } from '../../config/api.config';

interface GoalAIDecomposerProps {
  goal: Goal;
  onClose: () => void;
  onSuccess: () => void;
}

interface AISubGoal {
  name: string;
  description: string;
  level: number;
  complexityScore: number;
  estimatedHours: number;
  standalonePotential: boolean;
  aiReasoning: string;
}

interface GoalDecomposition {
  id: string;
  goalId: string;
  confidence: number;
  processingTime: number;
  generatedGoals: AISubGoal[];
  identifiedDependencies?: Array<{
    source: string;
    target: string;
    type: string;
    strength: number;
    description: string;
  }>;
  recommendations?: string[];
  riskFactors?: Array<{
    risk: string;
    probability: number;
    impact: string;
    mitigation: string;
  }>;
}

export const GoalAIDecomposer: React.FC<GoalAIDecomposerProps> = ({
  goal,
  onClose,
  onSuccess
}) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [decomposition, setDecomposition] = useState<GoalDecomposition | null>(null);
  const [selectedGoals, setSelectedGoals] = useState<Set<number>>(new Set());
  const [creatingGoals, setCreatingGoals] = useState(false);
  const [showContextModal, setShowContextModal] = useState(false);
  const [debugInfo, setDebugInfo] = useState<any>(null);

  const handleStartDecomposition = () => {
    setShowContextModal(true);
  };

  const handleContextSubmit = async (contextAnswers: Record<string, any>) => {
    setShowContextModal(false);
    setLoading(true);
    setError('');

    try {
      const token = localStorage.getItem('projectai_token');

      if (!token) {
        throw new Error('Please login again - no authentication token found');
      }

      // Use enhanced decomposition API with context
      const response = await fetch(apiConfig.endpoints.ai.decomposeWithContext, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          goalId: goal.id,
          contextAnswers,
          additionalContext: {
            focus: 'detailed implementation planning',
            constraints: 'scalable and maintainable architecture'
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'AI decomposition failed');
      }

      const data = await response.json();
      setDecomposition(data.decomposition);
      setDebugInfo(data.debug);

      // Pre-select all goals by default
      setSelectedGoals(new Set(data.decomposition.generatedGoals.map((_: any, index: number) => index)));

    } catch (err) {
      console.error('AI Decomposition failed:', err);
      setError('Failed to decompose goal. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateSubGoals = async () => {
    if (!decomposition || selectedGoals.size === 0) return;

    setCreatingGoals(true);
    setError('');

    try {
      // Create selected sub-goals
      const promises = Array.from(selectedGoals).map(async (index) => {
        const subGoal = decomposition.generatedGoals[index];

        const goalData = {
          projectId: goal.projectId,
          parentGoalId: goal.id,
          name: subGoal.name,
          description: subGoal.description,
          estimatedHours: subGoal.estimatedHours,
          complexityScore: subGoal.complexityScore,
          standalonePotential: subGoal.standalonePotential,
          aiGenerated: true,
          aiReasoning: subGoal.aiReasoning
        };

        return apiService.createGoal(goalData);
      });

      await Promise.all(promises);
      onSuccess();

    } catch (err) {
      console.error('Failed to create sub-goals:', err);
      setError('Failed to create sub-goals. Please try again.');
    } finally {
      setCreatingGoals(false);
    }
  };

  const toggleGoalSelection = (index: number) => {
    const newSelection = new Set(selectedGoals);
    if (newSelection.has(index)) {
      newSelection.delete(index);
    } else {
      newSelection.add(index);
    }
    setSelectedGoals(newSelection);
  };

  const getComplexityColor = (complexity: number) => {
    if (complexity <= 3) return 'text-green-600 bg-green-50';
    if (complexity <= 6) return 'text-yellow-600 bg-yellow-50';
    return 'text-red-600 bg-red-50';
  };

  return (
    <div className="fixed inset-0 z-60 overflow-y-auto">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-black bg-opacity-50" onClick={onClose} />

        <div className="relative bg-white rounded-lg shadow-xl max-w-4xl w-full overflow-hidden flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <div className="flex items-center gap-3">
              <Brain className="w-6 h-6 text-purple-600" />
              <div>
                <h2 className="text-xl font-semibold text-gray-900">
                  AI Goal Decomposition
                </h2>
                <p className="text-sm text-gray-600">
                  Break down "{goal.name}" into detailed sub-goals
                </p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          {/* Content */}
          <div className="overflow-y-auto flex-1">
            <div className="p-6">
              {!decomposition && !loading && (
                <div className="text-center py-12">
                  <Brain className="mx-auto w-16 h-16 text-purple-400 mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    Ready to Decompose Goal
                  </h3>
                  <p className="text-gray-600 mb-6 max-w-md mx-auto">
                    AI will analyze "{goal.name}" and create detailed sub-goals with
                    complexity scores, time estimates, and implementation recommendations.
                  </p>
                  <button
                    onClick={handleStartDecomposition}
                    className="btn-primary flex items-center gap-2 mx-auto"
                  >
                    <Brain className="w-4 h-4" />
                    Start AI Decomposition
                  </button>
                </div>
              )}

              {/* Context Question Modal */}
              <ContextQuestionModal
                isOpen={showContextModal}
                onClose={() => setShowContextModal(false)}
                goalId={goal.id}
                goalName={goal.name}
                onSubmit={handleContextSubmit}
                loading={loading}
              />

              {loading && (
                <div className="text-center py-12">
                  <Loader className="mx-auto w-12 h-12 text-purple-600 animate-spin mb-4" />
                  <h3 className="text-lg font-medium text-gray-900 mb-2">
                    AI is analyzing your goal...
                  </h3>
                  <p className="text-gray-600">
                    This may take 10-30 seconds depending on complexity
                  </p>
                </div>
              )}

              {error && (
                <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded mb-6">
                  <div className="flex items-center gap-2">
                    <AlertTriangle className="w-4 h-4" />
                    {error}
                  </div>
                </div>
              )}

              {decomposition && (
                <div className="space-y-6">
                  {/* AI Results Overview */}
                  <div className="bg-purple-50 border border-purple-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-3">
                      <CheckCircle className="w-5 h-5 text-purple-600" />
                      <h3 className="font-medium text-purple-900">
                        AI Analysis Complete
                      </h3>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                      <div>
                        <span className="text-purple-700 font-medium">Confidence:</span>
                        <span className="ml-2 text-purple-900">{Math.round(decomposition.confidence * 100)}%</span>
                      </div>
                      <div>
                        <span className="text-purple-700 font-medium">Processing Time:</span>
                        <span className="ml-2 text-purple-900">{(decomposition.processingTime / 1000).toFixed(1)}s</span>
                      </div>
                      <div>
                        <span className="text-purple-700 font-medium">Sub-Goals:</span>
                        <span className="ml-2 text-purple-900">{decomposition.generatedGoals.length}</span>
                      </div>
                    </div>
                  </div>

                  {/* Generated Sub-Goals */}
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-4">
                      Generated Sub-Goals
                      <span className="text-sm font-normal text-gray-600 ml-2">
                        (Select goals to create)
                      </span>
                    </h3>

                    <div className="space-y-3">
                      {decomposition.generatedGoals.map((subGoal, index) => (
                        <div
                          key={index}
                          className={`border rounded-lg p-4 cursor-pointer transition-all ${
                            selectedGoals.has(index)
                              ? 'border-purple-300 bg-purple-50'
                              : 'border-gray-200 hover:border-gray-300'
                          }`}
                          onClick={() => toggleGoalSelection(index)}
                        >
                          <div className="flex items-start gap-3">
                            <input
                              type="checkbox"
                              checked={selectedGoals.has(index)}
                              onChange={() => toggleGoalSelection(index)}
                              className="mt-1 rounded border-gray-300 text-purple-600 focus:ring-purple-500"
                            />

                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-2">
                                <Target className="w-4 h-4 text-gray-400 flex-shrink-0" />
                                <h4 className="font-medium text-gray-900">{subGoal.name}</h4>

                                {subGoal.standalonePotential && (
                                  <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-100 text-amber-800">
                                    Standalone Potential
                                  </span>
                                )}
                              </div>

                              <p className="text-gray-600 text-sm mb-3">
                                {subGoal.description}
                              </p>

                              <div className="flex items-center gap-4 text-xs">
                                <div className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  <span>{subGoal.estimatedHours}h</span>
                                </div>
                                <div className="flex items-center gap-1">
                                  <BarChart3 className="w-3 h-3" />
                                  <span className={`px-2 py-1 rounded-full font-medium ${getComplexityColor(subGoal.complexityScore)}`}>
                                    {subGoal.complexityScore}/10
                                  </span>
                                </div>
                              </div>

                              {subGoal.aiReasoning && (
                                <div className="mt-3 p-2 bg-gray-50 rounded text-xs text-gray-700">
                                  <strong>AI Insight:</strong> {subGoal.aiReasoning}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Implementation Notes */}
                  {decomposition.recommendations && decomposition.recommendations.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        Implementation Recommendations
                      </h3>
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                        <ul className="space-y-2 text-sm text-blue-800">
                          {decomposition.recommendations.map((note, index) => (
                            <li key={index} className="flex items-start gap-2">
                              <span className="text-blue-600 mt-1">•</span>
                              {note}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  )}

                  {/* Dependencies */}
                  {decomposition.identifiedDependencies && decomposition.identifiedDependencies.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        Identified Dependencies
                      </h3>
                      <div className="space-y-2">
                        {decomposition.identifiedDependencies.map((dep, index) => (
                          <div key={index} className="bg-yellow-50 border border-yellow-200 rounded p-3 text-sm">
                            <div className="font-medium text-yellow-900">
                              {dep.source} → {dep.target}
                            </div>
                            <div className="text-yellow-700 mt-1">
                              {dep.description} (Strength: {dep.strength}/5)
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Risk Factors */}
                  {decomposition.riskFactors && decomposition.riskFactors.length > 0 && (
                    <div>
                      <h3 className="text-lg font-semibold text-gray-900 mb-3">
                        Risk Factors
                      </h3>
                      <div className="space-y-2">
                        {decomposition.riskFactors.map((risk, index) => (
                          <div key={index} className="bg-red-50 border border-red-200 rounded p-3 text-sm">
                            <div className="font-medium text-red-900">
                              {risk.risk}
                            </div>
                            <div className="text-red-700 mt-1">
                              Probability: {Math.round(risk.probability * 100)}% | Impact: {risk.impact}
                            </div>
                            <div className="text-red-600 mt-1 text-xs">
                              <strong>Mitigation:</strong> {risk.mitigation}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Debug Information */}
          {debugInfo && (
            <div className="px-6 pb-6">
              <PromptDebugger
                title="Goal Decomposition AI Prompt"
                prompt={debugInfo.prompt}
                response={debugInfo.rawResponse}
                metadata={debugInfo.metadata}
              />
            </div>
          )}

          {/* Footer */}
          {decomposition && (
            <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200 bg-gray-50 min-h-[80px]">
              <div className="text-sm text-gray-600">
                {selectedGoals.size} of {decomposition.generatedGoals.length} sub-goals selected
              </div>

              <div className="flex items-center gap-3">
                <button
                  onClick={onClose}
                  className="btn-secondary"
                >
                  Cancel
                </button>
                <button
                  onClick={handleCreateSubGoals}
                  disabled={selectedGoals.size === 0 || creatingGoals}
                  className="btn-primary flex items-center gap-2"
                >
                  <Target className="w-4 h-4" />
                  {creatingGoals ? 'Creating...' : `Create ${selectedGoals.size} Sub-Goal${selectedGoals.size !== 1 ? 's' : ''}`}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
